/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataState } from '@/shared/ui';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { SettingsTabs } from './SettingsTabs';
import { SettingsSectionPicker } from './SettingsSectionPicker';
import { SettingsMobileMenu } from './SettingsMobileMenu';
import { ChevronLeftIcon } from '@/shared/ui/radix-icons';
import { UnstyledButton } from '@/shared/ui';
import { SettingsHeader } from './SettingsHeader';
import { SettingsSectionHeading } from './SettingsSectionHeading';
import { SettingsSectionContent } from './SettingsSectionContent';
import { SettingsPendingBand } from './SettingsPendingBand';
import { SettingsSaveBar } from './SettingsSaveBar';
import { SettingsLeaveConfirm } from './SettingsLeaveConfirm';
import { SettingsLoadingSkeleton } from './SettingsLoadingSkeleton';
import { SettingsSearch } from './SettingsSearch';
import { itemFor, type SettingsTarget } from '../constants/settings-items';
import { SettingsErrorState } from './SettingsErrorState';
import { useSettingsData } from '../hooks/use-settings-data';
import { useSettingsPending } from '../hooks/use-settings-pending';
import { useGuardedNavigate } from '../hooks/use-guarded-navigate';
import { SettingsDirtyProvider } from '../hooks/use-settings-dirty';
import { PortalSettingsProvider } from '../hooks/portal-settings-context';
import { isSettingsSectionVisible } from '../constants/settings-nav';
import { isSoloBrokerAgency } from '@/shared/utils/agency-type';
import { useAgencyPermissions } from '@/features/agency';
import type { SettingsNavOptions, SettingsSection } from '../constants/settings-nav';

const VALID_SECTIONS = new Set<SettingsSection>([
  'perfil', 'equipe', 'permissoes', 'plano', 'portais', 'facebook', 'vendas',
  'locacoes', 'imoveis',
]);

// The sale settings section was called `papelada` while the paperwork
// milestones were the only thing in it. Bookmarks and older links still
// carry that name, so they land where they always did rather than being
// bounced to the profile.
const RENAMED_SECTIONS: Record<string, SettingsSection> = { papelada: 'vendas' };

/**
 * Reads the active settings section from the `section` URL query parameter.
 * @returns A valid settings section, defaulting to `perfil` when absent or invalid.
 */
function readSectionFromUrl(): SettingsSection {
  try {
    const raw = new URLSearchParams(window.location.search).get('section') ?? '';
    const param = RENAMED_SECTIONS[raw] ?? (raw as SettingsSection);
    return VALID_SECTIONS.has(param) ? param : 'perfil';
  } catch {
    return 'perfil';
  }
}

/**
 * Reads the tab and the setting open in it from the URL, so a search result,
 * a shortcut or a bookmark can open one setting directly.
 * @returns The target, with no item when the URL names none or a wrong one.
 */
function readTargetFromUrl(): SettingsTarget {
  const section = readSectionFromUrl();
  try {
    return { section, item: itemFor(section, new URLSearchParams(window.location.search).get('item')) };
  } catch {
    return { section, item: null };
  }
}

/** Whether the URL already names a section, i.e. somebody linked straight in. */
function hasSectionInUrl(): boolean {
  try {
    return new URLSearchParams(window.location.search).has('section');
  } catch {
    return false;
  }
}

/**
 * The tab strip, in the form the viewport can carry.
 *
 * The narrow form is not a different control — `SettingsSectionPicker` is the
 * same eight sections with the same gating, scrolled by arrows instead of
 * laid out across the top.
 *
 * @param props - Component props.
 * @returns The section control.
 */
function SectionControl(props: {
  isMobile: boolean;
  active: SettingsSection;
  onNavigate: (section: SettingsSection) => void;
  navOptions: SettingsNavOptions;
  pendingSections: Set<SettingsSection>;
}) {
  const { t } = useTranslation('features/agency-settings');
  const { isMobile, active, onNavigate, navOptions, pendingSections } = props;

  if (isMobile) {
    return (
      <SettingsSectionPicker
        active={active}
        onNavigate={onNavigate}
        ariaLabel={t('header.sectionPickerAriaLabel')}
        isSoloBroker={Boolean(navOptions.isSoloBroker)}
      />
    );
  }

  return (
    <SettingsTabs
      active={active}
      onNavigate={onNavigate}
      navOptions={navOptions}
      pendingSections={pendingSections}
      ariaLabel={t('header.navAriaLabel')}
    />
  );
}

/**
 * The settings surface, inside the providers its hooks depend on.
 *
 * @param props - Component props.
 * @param props.agencyName - Name of the active agency.
 * @param props.navOptions - The gates that decide which sections are reachable.
 * @returns The surface element.
 */
function SettingsSurface({ agencyName, navOptions }: { agencyName: string; navOptions: SettingsNavOptions }) {
  const { t } = useTranslation('features/agency-settings');
  const isMobile = useIsMobile();
  const [target, setTarget] = useState<SettingsTarget>(readTargetFromUrl);
  /*
   * On a phone the sections are a menu, and the menu is a place you can be:
   * arriving without a section in the URL means "show me the menu", not
   * "show me Perfil". The desktop has no such state — its tab strip is
   * always visible, so a section is always open.
   */
  const [showMenu, setShowMenu] = useState(() => !hasSectionInUrl());
  const pending = useSettingsPending(navOptions);

  // A solo broker can land on `?section=equipe` from an old link or bookmark;
  // fall back to the profile section instead of rendering a hidden section.
  const active = isSettingsSectionVisible(target.section, navOptions) ? target.section : 'perfil';
  const openItem = active === target.section ? target.item : null;

  const navigate = useCallback((next: SettingsTarget) => {
    setTarget(next);
    setShowMenu(false);
    const url = new URL(window.location.href);
    url.searchParams.set('section', next.section);
    if (next.item) url.searchParams.set('item', next.item);
    else url.searchParams.delete('item');
    history.replaceState(null, '', url.toString());
  }, []);

  const guard = useGuardedNavigate(navigate);
  const requestSection = useCallback(
    (section: SettingsSection) => guard.request({ section, item: null }),
    [guard],
  );

  return (
    <div
      className="flex flex-col gap-(--space-5) p-(--space-5) min-h-[640px]"
      style={{ fontFamily: 'var(--font-family)' }}
    >
      <SettingsHeader
        title={t('header.title')}
        agencyName={agencyName}
        isMobile={isMobile}
        search={<SettingsSearch navOptions={navOptions} onNavigate={guard.request} />}
      />
      <SettingsPendingBand items={pending.items} onNavigate={requestSection} />

      {isMobile && showMenu ? (
        <SettingsMobileMenu
          onNavigate={requestSection}
          navOptions={navOptions}
          pendingItems={pending.items}
        />
      ) : (
        <>
          {isMobile ? (
            <UnstyledButton
              data-testid="settings-back-to-menu"
              onClick={() => setShowMenu(true)}
              className="flex items-center gap-(--space-1) self-start text-(length:--text-body-sm) text-[var(--text-secondary)]"
              style={{ minHeight: 44 }}
            >
              <ChevronLeftIcon width={16} height={16} aria-hidden="true" />
              {t('header.title')}
            </UnstyledButton>
          ) : (
            <SectionControl
              isMobile={isMobile}
              active={active}
              onNavigate={requestSection}
              navOptions={navOptions}
              pendingSections={pending.sections}
            />
          )}
          <main className="flex flex-col gap-(--space-5) min-w-0">
            {openItem ? (
              // Inside a setting the way out replaces the heading: the
              // block's own title already says where the user is.
              <UnstyledButton
                data-testid="settings-back-to-items"
                onClick={() => requestSection(active)}
                className="flex items-center gap-(--space-1) self-start text-(length:--text-body-sm) text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                style={{ minHeight: 44 }}
              >
                <ChevronLeftIcon width={16} height={16} aria-hidden="true" />
                {t(`v2.sections.${active}.title`)}
              </UnstyledButton>
            ) : (
              <SettingsSectionHeading section={active} />
            )}
            <SettingsSectionContent
              active={active}
              item={openItem}
              onOpenItem={(item) => guard.request({ section: active, item })}
            />
            <SettingsSaveBar />
          </main>
        </>
      )}
      <SettingsLeaveConfirm
        opened={guard.pending !== null}
        onStay={guard.cancelLeave}
        onLeave={guard.confirmLeave}
      />
    </div>
  );
}

/**
 * Root page for the redesigned agency settings experience.
 *
 * Resolves the agency and its gates, then hands the surface to the providers
 * that carry the shared unsaved-changes channel and the single portal fetch.
 *
 * @returns The settings page element.
 */
export function SettingsV2Page() {
  const { agency, isLoading, isError } = useSettingsData();
  const {
    canManageSalePaperworkMilestones, canManageLeaseSettings, canManagePropertySettings,
  } = useAgencyPermissions();
  const handleRetry = useCallback(() => window.location.reload(), []);

  const navOptions = useMemo<SettingsNavOptions>(() => ({
    isSoloBroker: isSoloBrokerAgency(agency.type),
    canManageSalePaperwork: canManageSalePaperworkMilestones,
    canManageLeaseSettings,
    canManagePropertySettings,
  }), [agency.type, canManageSalePaperworkMilestones, canManageLeaseSettings, canManagePropertySettings]);

  if (isLoading || isError) {
    return (
      <DataState
        isLoading={isLoading}
        isError={isError}
        isEmpty={false}
        onRetry={handleRetry}
        loadingFallback={<SettingsLoadingSkeleton />}
        errorState={<SettingsErrorState onRetry={handleRetry} />}
      >
        {null}
      </DataState>
    );
  }

  return (
    <SettingsDirtyProvider>
      <PortalSettingsProvider>
        <SettingsSurface agencyName={agency.name ?? ''} navOptions={navOptions} />
      </PortalSettingsProvider>
    </SettingsDirtyProvider>
  );
}
